import { useNavigate } from "react-router-dom";
import { useProfileForm } from "../hooks/complete-profile";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import { useEffect } from "react";
import { CompleteProfile } from "../../services/profile.services";
import Swal from "sweetalert2"; // for alert
import { SWAL_SETTINGS, ROLE } from "../../../../utils/Constants";
import { getUser, setLocalKey } from "../../../../utils/commonfunction";

const FormSchema = z.object({
  country: z
    .string()
    .trim()
    .min(2, { message: "Country must be at least 2 characters." })
    .regex(/^[a-zA-Z\s]+$/, { message: "Country must contain only letters and spaces." }),

  state: z
    .string()
    .trim()
    .min(2, { message: "State must be at least 2 characters." })
    .regex(/^[a-zA-Z\s]+$/, { message: "State must contain only letters and spaces." }),

  city: z
    .string()
    .trim()
    .min(2, { message: "City must be at least 2 characters." })
    .regex(/^[a-zA-Z\s]+$/, { message: "City must contain only letters and spaces." }),

  zip_code: z.string().trim().min(2, { message: "Post code must be at least 2 characters." }).max(10, { message: "Post code must be at most 10 characters." }).regex(/^[A-Za-z0-9][A-Za-z0-9 -]{1,9}$/, { message: "Post code can contain only letters, numbers, spaces and hyphens." }),

  location: z.string().trim().min(2, { message: "Location must be at least 2 characters." }).max(100, { message: "Location must be at most 100 characters." }),

  address: z.string().trim().min(5, { message: "Address must be at least 5 characters." }).max(200, { message: "Address must be at most 200 characters." }),
});

export default function LocationDetail() {
  const navigate = useNavigate();
  const { formData, updateForm, clearForm } = useProfileForm();

  const userData = getUser(ROLE.BUSINESS);

  const form = useForm({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      country: userData?.country || "",
      state: userData?.state || "",
      city: userData?.city || "",
      zip_code: userData?.zip_code?.toString() || "",
      location: userData?.address|| "",
      address: userData?.address || "",
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
  } = form;

  const watchAll = useWatch({ control });

  useEffect(() => {
    updateForm(watchAll);
  }, [watchAll]);

  function trimAllStrings(obj) {
    return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, typeof v === "string" ? v.trim() : v]));
  }

  async function onSubmit(data) {
    // Trim all string fields in formData before submitting
    const trimmedData = trimAllStrings(formData);

    try {
      const response = await CompleteProfile(trimmedData);
      if (response.success) {
        Swal.fire({
          icon: "success",
          text: response.message,
          ...SWAL_SETTINGS,
        });
        updateForm({ isProfileComplete: true });
        const businessJson = getUser(ROLE.BUSINESS);
        if (businessJson && Object.keys(businessJson).length) {
          businessJson.isCompleteProfile = true;
          setLocalKey(ROLE.BUSINESS, JSON.stringify(businessJson));
        }
        clearForm();
        navigate("/business/dashboard");
      } else {
        clearForm();
        Swal.fire({
          icon: "error",
          text: response.message,
          ...SWAL_SETTINGS,
        });
      }
    } catch (error) {
      clearForm();
      console.error("Submission error:", error);
      Swal.fire({
        icon: "error",
        title: "Submission Failed",
        text: error?.response?.data?.message || "Something went wrong!",
      });
    }
  }

  return (
    <div className="container section-section">
      <div className="back-button">
        <a href="/business/complete-profile/opening-hours" className="d-flex align-items-center gap-2">
          <i className="fi-rr-arrow-small-left"></i> <span>Back</span>
        </a>
      </div>
      <h3 className="h3-headline text-center my-5">Business Profile Setup</h3>
      <div className="setup-box mx-auto">
        <h4 class="h4-headline text-center mb-4">Location Details</h4>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="row g-3 text-start">
            {/* Location */}
            <div className="col-md-6">
              <label className="form-label subtitle-1" htmlFor="location">
                Location<span className="requirestar">*</span>
              </label>
              <input {...register("location")} type="text" className="form-control" id="location" placeholder="Enter location" />
              {errors.location && <p className="text-danger mt-1">{errors.location.message}</p>}
            </div>

            {/* Address */}
            <div className="col-md-6">
              <label className="form-label subtitle-1" htmlFor="address">
                Address<span className="requirestar">*</span>
              </label>
              <input {...register("address")} type="text" className="form-control" id="address" placeholder="Enter address" />
              {errors.address && <p className="text-danger mt-1">{errors.address.message}</p>}
            </div>

            {/* Country */}
            <div className="col-md-6">
              <label className="form-label subtitle-1" htmlFor="country">
                Country<span className="requirestar">*</span>
              </label>
              <input {...register("country")} type="text" className="form-control" id="country" placeholder="Enter country" />
              {errors.country && <p className="text-danger mt-1">{errors.country.message}</p>}
            </div>

            {/* State */}
            <div className="col-md-6">
              <label className="form-label subtitle-1" htmlFor="state">
                State<span className="requirestar">*</span>
              </label>
              <input {...register("state")} type="text" className="form-control" id="state" placeholder="Enter state" />
              {errors.state && <p className="text-danger mt-1">{errors.state.message}</p>}
            </div>

            {/* City */}
            <div className="col-md-6">
              <label className="form-label subtitle-1" htmlFor="city">
                City<span className="requirestar">*</span>
              </label>
              <input {...register("city")} type="text" className="form-control" id="city" placeholder="Enter city" />
              {errors.city && <p className="text-danger mt-1">{errors.city.message}</p>}
            </div>

            {/* Post Code */}
            <div className="col-md-6">
              <label className="form-label subtitle-1" htmlFor="zip_code">
                Post Code<span className="requirestar">*</span>
              </label>
              <input
                {...register("zip_code")}
                type="text"
                maxLength={10}
                className="form-control"
                id="zip_code"
                placeholder="Enter post code"
              />
              {errors.zip_code && <p className="text-danger mt-1">{errors.zip_code.message}</p>}
            </div>
          </div>

          <div className="text-center mt-5">
            <button type="submit" className="btn btn-light-purple px-5">
              Submit
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
